import { forwardRef, type Ref, type ReactNode, type InputHTMLAttributes } from 'react';
import { useStableId, useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import './Checkbox.css';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'onChange' | 'value' | 'type' | 'defaultChecked' | 'checked'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  indeterminate?: boolean;
}

export const Checkbox = forwardRef(function Checkbox(
  props: CheckboxProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const {
    label, description, error,
    checked, defaultChecked, onChange,
    size = 'sm', indeterminate, id, name, disabled,
    ...inputProps
  } = props;

  const auto = useStableId('rr-checkbox');
  const inputId = id ?? auto;
  const [val, setVal] = useUncontrolled<boolean>({
    value: checked,
    defaultValue: defaultChecked,
    finalValue: false,
    onChange
  });

  return (
    <label htmlFor={inputId} className={`rr-checkbox rr-checkbox--size-${size}`}>
      <input
        ref={(node) => {
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
          if (node) node.indeterminate = !!indeterminate;
        }}
        id={inputId}
        type="checkbox"
        name={name}
        checked={val}
        disabled={disabled}
        aria-invalid={!!error || undefined}
        onChange={(e) => setVal(e.currentTarget.checked)}
        {...inputProps}
      />
      <span className="rr-checkbox__box" aria-hidden>
        <svg className="rr-checkbox__check" viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1,5 4,8 11,1" />
        </svg>
      </span>
      <span className="rr-checkbox__body">
        {label ? <span className="rr-checkbox__label">{label}</span> : null}
        {description ? <span className="rr-checkbox__description">{description}</span> : null}
        {error ? <span className="rr-checkbox__error">{error}</span> : null}
      </span>
    </label>
  );
});
